/**
 * /admin/system/demo-tools — 先导小组演示数据工具
 *
 * 仅 tech_admin 可见。
 * 三个操作:
 *  1. 生成演示数据 (幂等 · 已存在则跳过)
 *  2. 重置演示数据 (删除 demo tenant 所有数据 → 重新 seed)
 *  3. 下载演示账号清单 (JSON → 浏览器触发下载)
 */
import { useState, useEffect, useCallback } from "react";
import { Database, RefreshCw, Download, CheckCircle, AlertCircle, Loader2, Info } from "lucide-react";
import { useAuthStore } from "@/store/authStore";

const API = "/api/v1/admin/demo";

interface DemoStatus {
  exists: boolean;
  tenant_slug: string;
  counts?: Record<string, number>;
  message?: string;
}

interface SeedResult {
  status: "ok" | "already_running" | "error";
  message: string;
  users: number;
  hotels: number;
  vehicles: number;
  drivers: number;
  products: number;
  schedules: number;
  channel_partners: number;
  dispatch_orders: number;
}

interface DemoAccount {
  role: string;
  email: string;
  password: string;
  description: string;
}

type OpState = "idle" | "loading" | "success" | "error";

function apiHeaders() {
  const token = useAuthStore.getState().accessToken;
  return {
    "Content-Type": "application/json",
    "X-Tenant-Slug": "default",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Status badge
// ─────────────────────────────────────────────────────────────────────────────

function StatusBadge({ exists }: { exists: boolean }) {
  if (exists) {
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 ring-1 ring-emerald-600/20">
        <CheckCircle className="h-3.5 w-3.5" />
        演示数据已就绪
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-300">
      <Info className="h-3.5 w-3.5" />
      尚未生成演示数据
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Count grid
// ─────────────────────────────────────────────────────────────────────────────

function CountGrid({ counts }: { counts: Record<string, number> }) {
  const labels: Record<string, string> = {
    users: "演示用户",
    hotels: "酒店",
    vehicles: "车辆",
    drivers: "司导",
    products: "产品",
    product_schedules: "班次排期",
    channel_partners: "同业 B2B",
    dispatch_orders: "调度订单",
  };
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
      {Object.entries(labels).map(([key, label]) => (
        <div key={key} className="rounded-lg border border-slate-200 bg-white px-4 py-3">
          <p className="text-xs text-slate-500">{label}</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">
            {counts[key] ?? "—"}
          </p>
        </div>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Result summary
// ─────────────────────────────────────────────────────────────────────────────

function SeedResultSummary({ result }: { result: SeedResult }) {
  return (
    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
      <div className="flex items-center gap-2 text-emerald-800">
        <CheckCircle className="h-4 w-4" />
        <span className="font-medium">{result.message}</span>
      </div>
      <div className="mt-2 flex flex-wrap gap-4 text-xs text-emerald-700">
        <span>用户 {result.users}</span>
        <span>酒店 {result.hotels}</span>
        <span>车辆 {result.vehicles}</span>
        <span>司导 {result.drivers}</span>
        <span>产品 {result.products}</span>
        <span>班次 {result.schedules}</span>
        <span>B2B {result.channel_partners}</span>
        <span>订单 {result.dispatch_orders}</span>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Demo accounts table
// ─────────────────────────────────────────────────────────────────────────────

function AccountsTable({ accounts }: { accounts: DemoAccount[] }) {
  return (
    <div className="overflow-hidden rounded-lg border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-xs font-medium uppercase text-slate-500">
          <tr>
            <th className="px-4 py-3 text-left">角色</th>
            <th className="px-4 py-3 text-left">邮箱</th>
            <th className="px-4 py-3 text-left">密码</th>
            <th className="px-4 py-3 text-left">用途说明</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100 bg-white">
          {accounts.map((acc) => (
            <tr key={acc.email} className="hover:bg-slate-50">
              <td className="px-4 py-3">
                <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                  {acc.role}
                </span>
              </td>
              <td className="px-4 py-3 font-mono text-slate-700">{acc.email}</td>
              <td className="px-4 py-3 font-mono font-semibold text-slate-900">
                {acc.password}
              </td>
              <td className="px-4 py-3 text-slate-500">{acc.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────────────────────

export function DemoToolsPage() {
  const [status, setStatus] = useState<DemoStatus | null>(null);
  const [seedState, setSeedState] = useState<OpState>("idle");
  const [resetState, setResetState] = useState<OpState>("idle");
  const [seedResult, setSeedResult] = useState<SeedResult | null>(null);
  const [accounts, setAccounts] = useState<DemoAccount[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadStatus = useCallback(async () => {
    try {
      const r = await fetch(`${API}/status`, { headers: apiHeaders() });
      if (r.ok) setStatus(await r.json());
    } catch {
      // ignore status load failure
    }
  }, []);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  const handleSeed = async () => {
    setSeedState("loading");
    setError(null);
    setSeedResult(null);
    try {
      const r = await fetch(`${API}/seed`, {
        method: "POST",
        headers: apiHeaders(),
      });
      const data: SeedResult = await r.json();
      if (r.ok && data.status === "ok") {
        setSeedState("success");
        setSeedResult(data);
        await loadStatus();
      } else {
        setSeedState("error");
        setError(data.message || `请求失败 (${r.status})`);
      }
    } catch (e) {
      setSeedState("error");
      setError(String(e));
    }
  };

  const handleReset = async () => {
    if (!confirm("确认重置所有演示数据？\n这将删除 demo tenant 下所有数据并重新生成。\n⚠️ 不影响 default tenant。")) return;
    setResetState("loading");
    setSeedState("idle");
    setSeedResult(null);
    setError(null);
    try {
      const r = await fetch(`${API}/reset`, {
        method: "POST",
        headers: apiHeaders(),
      });
      const data: SeedResult = await r.json();
      if (r.ok && data.status === "ok") {
        setResetState("success");
        setSeedResult(data);
        await loadStatus();
      } else {
        setResetState("error");
        setError(data.message || `重置失败 (${r.status})`);
      }
    } catch (e) {
      setResetState("error");
      setError(String(e));
    }
  };

  const handleDownloadAccounts = async () => {
    try {
      const r = await fetch(`${API}/accounts`, { headers: apiHeaders() });
      if (!r.ok) throw new Error(`${r.status}`);
      const data = await r.json();
      setAccounts(data.accounts);
      // 触发 JSON 文件下载
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "demo-accounts.json";
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(`下载账号失败: ${String(e)}`);
    }
  };

  const isRunning = seedState === "loading" || resetState === "loading";

  return (
    <div className="mx-auto max-w-4xl space-y-8 p-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-600 text-white">
            <Database className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">演示数据工具</h1>
            <p className="text-sm text-slate-500">先导小组试用数据生成 · 仅 tech_admin 可见</p>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-slate-700">demo tenant 状态</h2>
          {status && <StatusBadge exists={status.exists} />}
        </div>
        {status?.counts && (
          <div className="mt-4">
            <CountGrid counts={status.counts} />
          </div>
        )}
        {status?.message && !status.exists && (
          <p className="mt-3 text-sm text-slate-500">{status.message}</p>
        )}
      </div>

      {/* Actions */}
      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
        <h2 className="mb-5 text-sm font-semibold text-slate-700">操作</h2>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          {/* Seed */}
          <button
            onClick={handleSeed}
            disabled={isRunning}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-2.5 text-sm font-medium text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {seedState === "loading" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Database className="h-4 w-4" />
            )}
            {seedState === "loading" ? "生成中…" : "生成演示数据"}
          </button>

          {/* Reset */}
          <button
            onClick={handleReset}
            disabled={isRunning}
            className="inline-flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-5 py-2.5 text-sm font-medium text-amber-800 shadow-sm transition hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {resetState === "loading" ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {resetState === "loading" ? "重置中…" : "重置演示数据"}
          </button>

          {/* Download Accounts */}
          <button
            onClick={handleDownloadAccounts}
            disabled={isRunning}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-5 py-2.5 text-sm font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4" />
            下载账号清单
          </button>
        </div>

        {/* Result */}
        {(seedState === "success" || resetState === "success") && seedResult && (
          <div className="mt-4">
            <SeedResultSummary result={seedResult} />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-4">
            <div className="flex items-center gap-2 text-red-800">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <span className="text-sm">{error}</span>
            </div>
          </div>
        )}
      </div>

      {/* Accounts preview (after download) */}
      {accounts && (
        <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-slate-700">演示账号清单</h2>
            <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700 ring-1 ring-amber-300">
              ⚠️ 仅用于先导小组试用
            </span>
          </div>
          <AccountsTable accounts={accounts} />
          <p className="mt-3 text-xs text-slate-400">
            Tenant slug: <code className="rounded bg-slate-100 px-1">demo</code>
            &nbsp;· 登录时选择 demo 租户
          </p>
        </div>
      )}

      {/* Warning footer */}
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <p className="text-xs text-slate-500">
          <strong>安全说明</strong>：演示工具仅操作 <code className="rounded bg-slate-200 px-1">demo</code> tenant，
          不影响 <code className="rounded bg-slate-200 px-1">default</code> tenant 的任何数据。
          重置操作不可撤销。本页仅 tech_admin 角色可见。
        </p>
      </div>
    </div>
  );
}
